
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { Project, ChangeOrder, UserSettings } from '../types.ts';

export const generateInvoice = async (
    project: Project,
    items: ChangeOrder[],
    user: Partial<UserSettings> | undefined
): Promise<string> => {
    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const { width, height } = page.getSize();
    const margin = 50;

    // -- Header --
    page.drawText('INVOICE', {
        x: width - margin - 100,
        y: height - margin,
        size: 24,
        font: boldFont,
        color: rgb(0, 0, 0),
    });

    page.drawText(`Date: ${new Date().toLocaleDateString()}`, {
        x: width - margin - 100,
        y: height - margin - 20,
        size: 10,
        font,
    });

    page.drawText(`Invoice #: INV-${Date.now().toString().slice(-6)}`, {
        x: width - margin - 100,
        y: height - margin - 35,
        size: 10,
        font,
    });

    // -- Company Info (From Settings) --
    const companyName = user?.organizationName || 'LeanControls Contractor';
    const operatorName = user?.userName || 'Authorized Rep';

    page.drawText(companyName.toUpperCase(), {
        x: margin,
        y: height - margin,
        size: 18,
        font: boldFont,
        color: rgb(0, 0, 0),
    });

    page.drawText('General Contractor', {
        x: margin,
        y: height - margin - 20,
        size: 10,
        font,
        color: rgb(0.5, 0.5, 0.5),
    });

    // -- Bill To --
    page.drawText('BILL TO:', {
        x: margin,
        y: height - margin - 80,
        size: 10,
        font: boldFont,
    });

    page.drawText(project.client, {
        x: margin,
        y: height - margin - 95,
        size: 12,
        font,
    });

    page.drawText(`Project: ${project.title}`, {
        x: margin,
        y: height - margin - 110,
        size: 10,
        font,
        color: rgb(0.5, 0.5, 0.5),
    });

    // -- Table Header --
    const tableTop = height - margin - 160;

    page.drawRectangle({
        x: margin,
        y: tableTop - 5,
        width: width - (margin * 2),
        height: 20,
        color: rgb(0.95, 0.95, 0.95),
    });

    page.drawText('DESCRIPTION / CHANGE ORDER', {
        x: margin + 10,
        y: tableTop,
        size: 9,
        font: boldFont,
    });

    page.drawText('AMOUNT', {
        x: width - margin - 80,
        y: tableTop,
        size: 9,
        font: boldFont,
    });

    // -- Line Items --
    let yPosition = tableTop - 30;
    let total = 0;

    for (const item of items) {
        // Description
        page.drawText(`${item.coNumber}: ${item.title}`, {
            x: margin + 10,
            y: yPosition,
            size: 10,
            font,
            maxWidth: 350,
        });

        // Amount
        const amountStr = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.amount);
        page.drawText(amountStr, {
            x: width - margin - 90,
            y: yPosition,
            size: 10,
            font,
        });

        total += item.amount;
        yPosition -= 25;
    }

    // -- Total --
    yPosition -= 20;
    page.drawLine({
        start: { x: margin, y: yPosition + 30 },
        end: { x: width - margin, y: yPosition + 30 },
        thickness: 1,
        color: rgb(0.9, 0.9, 0.9),
    });

    page.drawText('TOTAL DUE:', {
        x: width - margin - 180,
        y: yPosition,
        size: 12,
        font: boldFont,
    });

    const totalStr = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(total);
    page.drawText(totalStr, {
        x: width - margin - 90,
        y: yPosition,
        size: 12,
        font: boldFont,
    });


    // -- Footer --
    page.drawText('Authorized by:', {
        x: margin,
        y: margin + 40,
        size: 8,
        font,
        color: rgb(0.6, 0.6, 0.6),
    });

    page.drawText(operatorName, {
        x: margin,
        y: margin + 25,
        size: 10,
        font: boldFont,
    });

    page.drawText('Generated via LeanControls Pro', {
        x: width / 2 - 60,
        y: margin,
        size: 8,
        font,
        color: rgb(0.8, 0.8, 0.8),
    });

    const pdfBytes = await pdfDoc.save();
    // Using explicit any cast to avoid type conflicts with BlobPart
    const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
};
